package com.karyadina.cloudsync.service

import com.karyadina.cloudsync.model.SharedLink
import com.karyadina.cloudsync.model.SharedContent
import com.karyadina.cloudsync.util.TimeUtil
import com.karyadina.service.AppStorage
import kotlinx.serialization.json.Json
import kotlinx.serialization.encodeToString
import kotlinx.serialization.decodeFromString
import kotlin.io.encoding.Base64
import kotlin.io.encoding.ExperimentalEncodingApi

/**
 * Minimal Shared Link Service implementation
 */
class SharedLinkServiceImpl(
    private val storage: SharedLinkStorage,
    private val googleAuthService: GoogleAuthService
) : SharedLinkService {
    
    @OptIn(ExperimentalEncodingApi::class)
    override suspend fun createLink(
        itemId: String,
        itemType: String,
        itemTitle: String,
        expiryMinutes: Int
    ): Result<SharedLink> {
        val storageInstance = AppStorage.get()
        val dataToExport = mutableMapOf<String, String>()
        
        when (itemId) {
            "task_manager" -> {
                storageInstance.loadString("tasks")?.let { dataToExport["tasks"] = it }
                storageInstance.loadString("task_categories")?.let { dataToExport["task_categories"] = it }
            }
            "quick_notes" -> {
                storageInstance.loadString("quick_notes")?.let { dataToExport["quick_notes"] = it }
            }
            else -> {
                storageInstance.loadString(itemId)?.let { dataToExport[itemId] = it }
            }
        }
        
        val jsonString = Json.encodeToString(dataToExport)
        val base64Data = Base64.UrlSafe.encode(jsonString.encodeToByteArray())
        
        val link = SharedLink(
            id = TimeUtil.generateUUID(),
            creatorId = googleAuthService.getUserId() ?: "unknown",
            targetId = itemId,
            targetType = itemType,
            targetTitle = itemTitle,
            accessToken = base64Data,
            expiresAt = TimeUtil.currentTimeMillis() + (expiryMinutes * 60 * 1000),
            used = false,
            createdAt = TimeUtil.currentTimeMillis()
        )
        storage.saveLink(link)
        return Result.success(link)
    }
    
    @OptIn(ExperimentalEncodingApi::class)
    override suspend fun getSharedItem(token: String): Result<SharedContent> {
        return try {
            val decodedBytes = Base64.UrlSafe.decode(token)
            val jsonString = decodedBytes.decodeToString()
            
            Result.success(SharedContent(
                link = SharedLink(
                    id = "shared-id",
                    creatorId = "unknown",
                    targetId = "shared",
                    targetType = "data",
                    targetTitle = "Shared Snapshot",
                    accessToken = token,
                    expiresAt = TimeUtil.currentTimeMillis() + 1000000,
                    used = false,
                    createdAt = TimeUtil.currentTimeMillis()
                ),
                content = jsonString,
                contentSize = decodedBytes.size.toLong()
            ))
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
    
    override suspend fun validateToken(token: String): Result<Boolean> {
        return Result.success(true)
    }
    
    override suspend fun deleteLink(token: String): Result<Unit> {
        return Result.success(Unit)
    }
    
    override suspend fun getUserSharedLinks(): Result<List<SharedLink>> {
        val linksResult = storage.getAllLinks()
        return linksResult.map { links ->
            links.filter { link -> link.creatorId == googleAuthService.getUserId() }
        }
    }
    
    override suspend fun cleanupExpiredLinks(): Result<Int> {
        return storage.deleteExpiredLinks()
    }
}
